import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API, getAuthHeaders } from '../shared';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Search, Phone, Edit, MapPin, Tag, DollarSign, BellRing } from 'lucide-react';
import WhatsAppButton from './WhatsAppButton';

const PIPELINE_STAGES = [
  { id: 'lead', label: 'عميل محتمل', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  { id: 'interested', label: 'مهتم', color: 'bg-brand-100 text-brand-700 border-brand-300' },
  { id: 'negotiation', label: 'تفاوض', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'booked', label: 'محجوز', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { id: 'paid', label: 'مدفوع', color: 'bg-green-100 text-green-700 border-green-300' },
  { id: 'traveling', label: 'مسافر', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { id: 'completed', label: 'منتهٍ', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { id: 'lost', label: 'مفقود', color: 'bg-red-100 text-red-700 border-red-300' }
];

const CLIENT_TYPES = ['عمرة', 'حج', 'سفر', 'أخرى'];
const LEAD_SOURCES = ['facebook', 'instagram', 'whatsapp', 'توصية', 'زيارة مباشرة', 'اتصال هاتفي', 'إعلان جوجل', 'أخرى'];

const SalesPipeline = () => {
  useContext(AuthContext);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [crmDialog, setCrmDialog] = useState({ open: false, client: null });
  const [form, setForm] = useState({
    city: '', client_type: '', budget: '', tags: '', status: '', lead_source: '',
    assigned_user_id: '', notes: '', next_followup_date: ''
  });

  const fetchClients = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
      setClients((r.data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(r.data || []);
    } catch (e) { /* not all users have access */ }
  };

  useEffect(() => { fetchClients(); fetchUsers(); }, []);

  const openCrmDialog = (client) => {
    setForm({
      city: client.city || '',
      client_type: client.client_type || '',
      budget: client.budget != null ? String(client.budget) : '',
      tags: (client.tags || []).join(', '),
      status: client.status || 'lead',
      lead_source: client.lead_source || '',
      assigned_user_id: client.assigned_user_id || '',
      notes: client.notes || '',
      next_followup_date: client.next_followup_date || ''
    });
    setCrmDialog({ open: true, client });
  };

  const saveCrm = async () => {
    try {
      const payload = {
        city: form.city || null,
        client_type: form.client_type || null,
        budget: form.budget ? parseFloat(form.budget) : null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : null,
        status: form.status || null,
        lead_source: form.lead_source || null,
        assigned_user_id: form.assigned_user_id || null,
        notes: form.notes || null,
        next_followup_date: form.next_followup_date || null
      };
      await axios.patch(`${API}/crm/clients/${crmDialog.client.id}`, payload, { headers: getAuthHeaders() });
      setCrmDialog({ open: false, client: null });
      fetchClients();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في الحفظ'); }
  };

  const quickStatusChange = async (client, status) => {
    try {
      await axios.patch(`${API}/crm/clients/${client.id}`, { status }, { headers: getAuthHeaders() });
      fetchClients();
    } catch (e) { alert('خطأ'); }
  };

  const filtered = clients.filter(c => {
    const matchSearch = !search ||
      (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || '').includes(search) ||
      (c.city || '').toLowerCase().includes(search.toLowerCase());
    const matchStage = stageFilter === 'all' || (c.status || 'lead') === stageFilter;
    return matchSearch && matchStage;
  });

  const stageStat = (stageId) => clients.filter(c => (c.status || 'lead') === stageId).length;

  if (loading) return <div className="p-10 text-center text-gray-500">جاري التحميل...</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📋 Sales Pipeline — متابعة العملاء</h1>
          <p className="text-sm text-gray-600 mt-1">تصنيف العملاء حسب مراحل البيع، WhatsApp مباشر، تذكيرات متابعة</p>
        </div>
      </div>

      {/* Stage Filters */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={stageFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStageFilter('all')}
        >الكل ({clients.length})</Button>
        {PIPELINE_STAGES.map(s => (
          <Button
            key={s.id}
            variant={stageFilter === s.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStageFilter(s.id)}
            className={stageFilter === s.id ? '' : s.color}
          >
            {s.label} ({stageStat(s.id)})
          </Button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="ابحث بالاسم، الهاتف، المدينة..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pr-10"
        />
      </div>

      {/* Clients Grid */}
      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">لا توجد عملاء بهذه المعايير</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => {
            const stage = PIPELINE_STAGES.find(s => s.id === (c.status || 'lead')) || PIPELINE_STAGES[0];
            return (
              <Card key={c.id} className={`border-r-4 ${stage.color.split(' ').find(x => x.startsWith('border-')) || 'border-gray-300'}`}>
                <CardContent className="p-4 space-y-3">
                  {/* Top: name + stage badge */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">{c.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {c.phone || '—'}
                      </p>
                    </div>
                    <Badge className={stage.color}>{stage.label}</Badge>
                  </div>

                  {/* Meta */}
                  <div className="space-y-1 text-xs text-gray-600">
                    {c.city && <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.city}</div>}
                    {c.client_type && <div className="flex items-center gap-1"><Tag className="h-3 w-3" /> {c.client_type}</div>}
                    {c.budget && <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" /> ميزانية: {c.budget.toLocaleString()} دج</div>}
                    {c.next_followup_date && <div className="flex items-center gap-1 text-amber-600"><BellRing className="h-3 w-3" /> متابعة: {c.next_followup_date}</div>}
                  </div>

                  {/* Tags */}
                  {(c.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {c.tags.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                    </div>
                  )}

                  {/* Quick stage change */}
                  <Select value={c.status || 'lead'} onValueChange={v => quickStatusChange(c, v)}>
                    <SelectTrigger className="text-xs h-8" data-testid="pipeline-stage-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PIPELINE_STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  {/* Action buttons */}
                  <div className="flex gap-1 pt-1">
                    <div className="flex-1">
                      <WhatsAppButton
                        phone={c.phone}
                        clientName={c.name}
                        packageName={c.client_type || ''}
                        price={c.budget || ''}
                        variant="compact"
                        buttonText="WhatsApp"
                        defaultTemplate={c.status === 'booked' || c.status === 'paid' ? 'booking_confirm' : (c.status === 'lead' || c.status === 'interested' ? 'umrah_offer' : 'follow_up')}
                        className="w-full"
                      />
                    </div>
                    <Button size="sm" variant="outline" onClick={() => window.open(`tel:${c.phone}`)} className="flex-1">
                      <Phone className="h-4 w-4 ml-1 text-brand-600" /> اتصال
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openCrmDialog(c)} data-testid="edit-crm-btn">
                      <Edit className="h-4 w-4 text-brand-600" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* CRM Edit Dialog */}
      <Dialog open={crmDialog.open} onOpenChange={o => setCrmDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>تعديل بيانات CRM — {crmDialog.client?.name}</DialogTitle>
            <DialogDescription>المرحلة، المصدر، الميزانية، الوسوم، التذكيرات</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>المرحلة</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent>{PIPELINE_STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>نوع العميل</Label>
              <Select value={form.client_type} onValueChange={v => setForm({ ...form, client_type: v })}>
                <SelectTrigger><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent>{CLIENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>المدينة</Label>
              <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="وهران، الجزائر، تلمسان..." />
            </div>
            <div>
              <Label>مصدر العميل</Label>
              <Select value={form.lead_source} onValueChange={v => setForm({ ...form, lead_source: v })}>
                <SelectTrigger><SelectValue placeholder="من أين جاء؟" /></SelectTrigger>
                <SelectContent>{LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>الميزانية (دج)</Label>
              <Input type="number" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} placeholder="200000" />
            </div>
            <div>
              <Label>الموظف المسؤول</Label>
              <Select value={form.assigned_user_id} onValueChange={v => setForm({ ...form, assigned_user_id: v })}>
                <SelectTrigger><SelectValue placeholder="غير معيّن" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>الوسوم (مفصولة بفاصلة)</Label>
              <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="مثال: VIP, عميل قديم, دفع كاش" />
            </div>
            <div>
              <Label>تاريخ المتابعة القادم</Label>
              <Input type="date" value={form.next_followup_date} onChange={e => setForm({ ...form, next_followup_date: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>ملاحظات</Label>
              <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="ملاحظات حول العميل..." />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setCrmDialog({ open: false, client: null })}>إلغاء</Button>
            <Button onClick={saveCrm} className="bg-brand-600 hover:bg-brand-700" data-testid="save-crm-btn">حفظ</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesPipeline;
